<template>
    <div class="p-6 relative flex flex-col shrink-0 w-full space-y-5 rounded-lg bg-gray-950/50 overflow-hidden">
        <div class="flex items-center justify-between">
            <div>
                <p class="mb-1 text-xs text-gray-400 uppercase">Incident Location</p>
                <template v-if="address">
                    <p class="text-white font-light">{{ address.line_1 }}</p>
                    <p class="text-white font-light">{{ [address.city, address.region, address.country].filter(Boolean).join(', ') }}</p>
                </template>
            </div>
            <UButton @click="editDirections()" size="sm" color="white">
                {{ editingDirections ? 'Save' : 'Edit' }}
            </UButton>
        </div>

        <div v-if="address?.directions || editingDirections">
            <p class="mb-2 text-xs text-gray-400 uppercase">
                Directions
            </p>
            <UTextarea 
                v-if="editingDirections" 
                v-model="directions" 
                size="xl"
            />
            <p v-else-if="address?.directions" class="text-white font-light">
                {{ address.directions }}
            </p>
        </div>

        <div v-if="callLocation" class="relative w-full h-[160px] rounded-md overflow-hidden">
            <Map scroll-zoom-disabled :center="callLocation" />

            <a 
                v-if="callLocationLink" 
                :href="callLocationLink" 
                target="_blank" 
                class="group absolute inset-0 hover:bg-gray-800 hover:bg-opacity-30 transition-all"
            >
                <div class="absolute whitespace-nowrap top-5 left-1/2 transform -translate-x-1/2 rounded-lg bg-gray-500 bg-opacity-30 text-center text-xs text-white px-4 py-3 space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p class="space-x-1">
                        <span class="text-2xs font-medium font-mono py-1 px-1.5 rounded-md bg-gray-500">SHIFT + CLICK</span>
                        <span>to open in a new window</span>
                    </p>
                    <p class="space-x-1">
                        <span class="text-2xs font-medium font-mono py-1 px-1.5 rounded-md bg-gray-500">CTRL + CLICK</span>
                        <span>to open in a new tab</span>
                    </p>
                </div>
                <div v-if="address?.accuracy" class="absolute bottom-0 right-0 px-3.5 py-1.5 flex items-center justify-center rounded-br-lg bg-gray-500 bg-opacity-30">
                    <span class="text-xs text-white font-medium">Accuracy: {{ address.accuracy }}m.</span>
                </div>
            </a>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Address } from '~/types/address'

const props = defineProps<{
    address?: Address
}>()

const client = useSupabaseClient()

const editingDirections = ref<boolean>(false)
const directions = ref<string>('')

const callLocation = computed(() => {
    if (props.address?.latitude && props.address?.longitude) {
        return {
            lat: props.address.latitude,
            lng: props.address.longitude
        }
    }
})

const callLocationLink = computed(() => {
    if (props.address?.latitude && props.address?.longitude) {
        return 'https://google.com/maps/place/'
            + props.address.latitude + ','
            + props.address.longitude
    }
})

async function editDirections() {
    editingDirections.value = !editingDirections.value

    if (props.address) {
        if (editingDirections.value) {
            directions.value = props.address.directions || ''
        }
        else {
            await client.from('addresses').update({ directions: directions.value } as never).eq('id', props.address.id)
            props.address.directions = directions.value
        }
    }
}
</script>